import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, LocateFixed, Lock, MapPin, Users } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import { BlogFooter } from "../blog/BlogPages";
import { api } from "../rsvp/api";
import { whenText, whereText } from "./PlanPage";
import "../blog/blog.css";
import "../styles/EventsPage.css";
import "../styles/PlansPage.css";

// For people who'd rather not share their location.
const CITIES = [
  { name: "Lucknow", lat: 26.85, lng: 80.95 },
  { name: "Delhi", lat: 28.61, lng: 77.21 },
  { name: "Bangalore", lat: 12.97, lng: 77.59 },
  { name: "Mumbai", lat: 19.08, lng: 72.88 },
  { name: "Chennai", lat: 13.08, lng: 80.27 },
  { name: "Vellore", lat: 12.92, lng: 79.13 },
];

const distanceText = (km) => (km < 1 ? "Under 1 km away" : `${km % 1 ? km.toFixed(1) : km} km away`);

/** /plans: live plans near you ("I want to…" from the app). Joining happens in the app. */
const PlansPage = () => {
  // idle → locating → loading → done; denied/error when something stops it.
  const [state, setState] = useState("idle");
  const [where, setWhere] = useState("");
  const [plans, setPlans] = useState([]);

  const load = useCallback(async (lat, lng, label) => {
    setState("loading");
    setWhere(label);
    try {
      const res = await api.nearbyPlans(lat, lng);
      setPlans(res.data?.plans ?? []);
      setState("done");
    } catch (e) {
      console.error("Couldn't load plans:", e);
      setState("error");
    }
  }, []);

  const locate = useCallback(() => {
    if (!navigator.geolocation) { setState("denied"); return; }
    setState("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => load(pos.coords.latitude, pos.coords.longitude, "you"),
      (err) => setState(err.code === err.PERMISSION_DENIED ? "denied" : "error"),
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 10 * 60 * 1000 },
    );
  }, [load]);

  // Visitors who already allowed location go straight to their plans.
  useEffect(() => {
    navigator.permissions?.query({ name: "geolocation" })
      .then((p) => { if (p.state === "granted") locate(); })
      .catch(() => {});
  }, [locate]);

  const busy = state === "locating" || state === "loading";

  return (
    <main className="blog-page">
      <SiteHeader />
      <section className="blog-hero">
        <p className="blog-eyebrow">Happening around you</p>
        <h1>Plans</h1>
        <p className="blog-lede">Coffee, a run, a movie, a study session: see what people near you want to do, then join them in the Blox app. In the app you can also say you're free right now, match your crew with another crew, and hear voice notes people left around the city.</p>
      </section>

      <section className="blog-list" aria-live="polite">
        {(state === "idle" || state === "denied" || state === "error" || busy) && (
          <div className="plans-ask">
            <div className="plans-ask-icon" aria-hidden="true">📍</div>
            <h2>{state === "denied" ? "Location is turned off" : "Find plans near you"}</h2>
            <p>
              {state === "denied"
                ? "Allow location for bloxsocial.live in your browser settings, or pick a city below."
                : state === "error"
                  ? "We couldn't get your location or load plans. Try again, or pick a city below."
                  : "We use your location once to show plans within 25 km. It isn't stored or shared."}
            </p>
            <button type="button" className="plans-locate" onClick={locate} disabled={busy}>
              <LocateFixed size={17} /> {state === "locating" ? "Finding you…" : state === "loading" ? "Loading plans…" : "Use my location"}
            </button>
            <div className="plans-cities">
              <span>Or browse</span>
              {CITIES.map((c) => (
                <button key={c.name} type="button" disabled={busy} onClick={() => load(c.lat, c.lng, c.name)}>{c.name}</button>
              ))}
            </div>
          </div>
        )}

        {state === "done" && (
          <>
            <div className="plans-bar">
              <p>{plans.length ? `${plans.length} plan${plans.length === 1 ? "" : "s"}` : "No plans"} within 25 km of <strong>{where}</strong></p>
              <div className="plans-cities">
                <button type="button" onClick={locate}><LocateFixed size={14} /> Near me</button>
                {CITIES.filter((c) => c.name !== where).map((c) => (
                  <button key={c.name} type="button" onClick={() => load(c.lat, c.lng, c.name)}>{c.name}</button>
                ))}
              </div>
            </div>
            {plans.length === 0 ? (
              <p className="events-empty">Nothing planned nearby right now. Start one in the Blox app — someone nearby probably wants to do the same thing.</p>
            ) : (
              <div className="blog-grid">
                {plans.map((p, i) => (
                  <Link to={`/plan/${p.id}`} className="blog-card plans-card" key={p.id} style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
                    <div className="blog-card-body">
                      <div className="plans-top">
                        <span className="plans-emoji" aria-hidden="true">{p.category?.emoji || "✨"}</span>
                        <span className="plans-distance">{distanceText(p.distance_km)}</span>
                      </div>
                      <p className="blog-meta">
                        {p.category?.name && <span className="blog-chip">{p.category.name}</span>}
                        {p.join_mode === "approval" && <span className="plans-private"><Lock size={12} /> Host approves</span>}
                      </p>
                      <h3>I want to {p.title}</h3>
                      <p className="events-fact"><CalendarDays size={15} /> {whenText(p)}</p>
                      <p className="events-fact"><MapPin size={15} /> {whereText(p)}{p.city && !whereText(p).includes(p.city) ? `, ${p.city}` : ""}</p>
                      <p className="events-fact">
                        <Users size={15} /> {p.member_count} going{p.max_members ? ` · ${p.max_members} max` : ""}
                        {p.host?.first_name ? ` · hosted by ${p.host.first_name}` : ""}
                      </p>
                      <span className="blog-read events-cta">See plan →</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>
      <BlogFooter />
    </main>
  );
};

export default PlansPage;
